import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { commonApiOptions } from '../../helpers/api-helpers';
import { useRoiRequestPayload, type UseRoiRequestBaseProps } from '../../hooks/use-roi-request-payload';
import { generateNoShowRateAggregatesDemoData } from '../demo-data/overview';

interface Props extends UseRoiRequestBaseProps {
  noShowRateType: NoShowRateByType;
}

interface UseFetchNSROILocationsNoShowRate {
  data?: AnalyticsGatewayV1.Types.GetNoShowRateAggregatesIO['output'];
  isLoading?: boolean;
}

export const useFetchNSROILocationsNoShowRate = ({
  noShowRateType,
  ...rest
}: Props): UseFetchNSROILocationsNoShowRate => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);

  const { request, skipRequest } = useRoiRequestPayload({
    ...rest,
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType,
    },
    isDemoAccount,
  });

  const { data: noShowRateAggregates, isLoading } = AnalyticsGatewayV1.Queries.useGetNoShowRateAggregatesQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest,
      onError: () => {
        alert.error(t("Couldn't fetch the no-show rate details. Please try again."));
      },
    },
  });

  return {
    data: isDemoAccount
      ? generateNoShowRateAggregatesDemoData(rest.filters?.locationIds, noShowRateType)
      : noShowRateAggregates,
    isLoading,
  };
};
